/***
 *
 *   PROJECT SUPPORT LINK (settings)
 *   One-click API key + a copy-paste backend snippet. Valetify signs the
 *   intake URL; the customer's product never implements HMAC.
 *
 **********/

import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Animate, Card, Button, ViewContext, useProjectContext } from 'components/lib';
import { useTranslate } from 'app/translations';
import { useApiContext } from 'app/api';
import settings from '../../../settings';
import Style from './portal.tailwind.js';

function portalLinkSnippet(serverUrl, projectId) {
    const api = String(serverUrl || '').replace(/\/$/, '') || 'https://YOUR_API';
    return `const res = await fetch("${api}/api/project/${projectId}/portal-link", {
  method: "POST",
  headers: {
    Authorization: "Basic " + process.env.VALETIFY_API_KEY,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    email: user.email,
    first_name: user.firstName,
    last_name: user.lastName,
  }),
});
const json = await res.json();
if (!res.ok) throw new Error(json.message || "Valetify portal-link failed");
return json.data.url;`;
}

export default function ProjectPortal() {
    const { id } = useParams();
    const { _t } = useTranslate();
    const { callApi } = useApiContext();
    const viewContext = useContext(ViewContext);
    const { projects } = useProjectContext();
    const project = projects?.find((p) => p.id === id);

    const [apiKey, setApiKey] = useState(null);
    const [copied, setCopied] = useState('');
    const [busy, setBusy] = useState(false);
    const [rotateBusy, setRotateBusy] = useState(false);

    useEffect(() => {
        setApiKey(null);
        setCopied('');
    }, [id]);

    if (!project) return null;

    const snippet = portalLinkSnippet(settings.server_url, project.id);

    async function copy(label, value) {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            setCopied(label);
            window.setTimeout(() => setCopied(''), 2000);
        } catch (err) {
            setCopied('');
        }
    }

    async function createKey() {
        setBusy(true);
        try {
            const res = await callApi({
                method: 'post',
                url: `/api/project/${id}/portal-key`,
            });
            if (res.status < 200 || res.status >= 300) {
                viewContext.notification.show(
                    res.message || _t('project.portal.keyFailed'),
                    'error',
                    true,
                    'toast',
                );
                return;
            }
            setApiKey(res.data?.key || null);
            setCopied('');
        } finally {
            setBusy(false);
        }
    }

    async function rotate() {
        setRotateBusy(true);
        try {
            const res = await callApi({
                method: 'post',
                url: `/api/project/${id}/portal-secret`,
            });
            if (res.status < 200 || res.status >= 300) {
                viewContext.notification.show(
                    res.message || _t('project.portal.keyFailed'),
                    'error',
                    true,
                    'toast',
                );
                return;
            }
            viewContext.notification.show(_t('project.portal.rotated'), 'success', true, 'toast');
        } finally {
            setRotateBusy(false);
        }
    }

    function confirmRotate() {
        viewContext.modal.show(
            {
                title: _t('project.portal.rotate'),
                text: _t('project.portal.rotateConfirm'),
                form: {},
                buttonText: _t('project.portal.rotate'),
            },
            rotate,
        );
    }

    return (
        <Animate>
            <Card
                title={_t('project.portal.sectionTitle')}
                description={_t('project.portal.sectionDescription')}
                className="w-full">
                <ol className={Style.steps}>
                    <li className={Style.step}>
                        <p className={Style.stepTitle}>{_t('project.portal.stepKeyTitle')}</p>
                        <p className={Style.hint}>{_t('project.portal.stepKeyHint')}</p>
                        <div className={Style.footer}>
                            <Button
                                variant="primary"
                                size="sm"
                                text={
                                    apiKey
                                        ? _t('project.portal.createAnotherKey')
                                        : _t('project.portal.createKey')
                                }
                                isLoading={busy}
                                action={createKey}
                            />
                        </div>
                        {apiKey && (
                            <div className={Style.secretBlock}>
                                <p className={Style.warning}>{_t('project.portal.keyOnce')}</p>
                                <p className={Style.hint}>{_t('project.portal.keyEnvHint')}</p>
                                <div className={Style.row}>
                                    <input
                                        readOnly
                                        value={`VALETIFY_API_KEY=${apiKey}`}
                                        className={Style.secretInput}
                                        aria-label={_t('project.portal.keyOnce')}
                                    />
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        text={
                                            copied === 'key'
                                                ? _t('project.portal.copied')
                                                : _t('project.portal.copy')
                                        }
                                        action={() => copy('key', `VALETIFY_API_KEY=${apiKey}`)}
                                    />
                                </div>
                            </div>
                        )}
                    </li>
                    <li className={Style.step}>
                        <p className={Style.stepTitle}>{_t('project.portal.stepCodeTitle')}</p>
                        <p className={Style.hint}>{_t('project.portal.stepCodeHint')}</p>
                        <div className={Style.snippetHeader}>
                            <Button
                                variant="ghost"
                                size="sm"
                                text={
                                    copied === 'snippet'
                                        ? _t('project.portal.copied')
                                        : _t('project.portal.copy')
                                }
                                action={() => copy('snippet', snippet)}
                            />
                        </div>
                        <pre className={Style.pre}>{snippet}</pre>
                    </li>
                </ol>

                <details className={Style.advanced}>
                    <summary className={Style.advancedSummary}>
                        {_t('project.portal.advancedTitle')}
                    </summary>
                    <p className={Style.hint}>{_t('project.portal.rotateHint')}</p>
                    <div className={Style.footer}>
                        <Button
                            variant="secondary"
                            size="sm"
                            text={_t('project.portal.rotate')}
                            isLoading={rotateBusy}
                            action={confirmRotate}
                        />
                    </div>
                </details>
            </Card>
        </Animate>
    );
}
